import type { CardCodeEntry } from "modules/decks/model/deck";

/**
 * Detects if input is a Riftbound deck code (base32 string starting with "CI").
 * Deck codes are single-line base32-encoded strings.
 */
export function isRiftboundDeckCode(input: string): boolean {
  const trimmed = input.trim();
  if (trimmed.includes("\n")) return false;
  if (trimmed.length < 4) return false;
  if (!trimmed.startsWith("CI")) return false;
  return /^[A-Z2-7]+=*$/i.test(trimmed);
}

/**
 * Parses a Riftbound card code (e.g., "OGN-007", "OGN-007a") into set code and number.
 * Card codes are formatted as: SET-NUMBER[variant]
 * The number is returned as-is (including any variant suffix) to match DB storage.
 */
function parseCardCode(cardCode: string): { setCode: string; number: string } {
  // Card code format: "OGN-007" or "OGN-007a" (with variant suffix)
  const dashIndex = cardCode.indexOf("-");
  if (dashIndex === -1) {
    return { setCode: "", number: cardCode };
  }

  const setCode = cardCode.substring(0, dashIndex);
  // The part after the dash is the number, possibly with a variant suffix letter
  const numberPart = cardCode.substring(dashIndex + 1);
  return { setCode, number: numberPart };
}

/**
 * Decodes a Riftbound deck code into structured card entries.
 * Uses the @piltoverarchive/riftbound-deck-codes library.
 *
 * The library's getDeckFromCode returns a DeckWithSideboard:
 *   { mainDeck: Card[], sideboard: Card[], chosenChampion?: string }
 * where Card = { cardCode: string, count: number }
 *
 * We map mainDeck -> "Mainboard" location and sideboard -> "Sideboard" location.
 * The chosenChampion (if present) is placed in the "Legend" location.
 */
export async function decodeRiftboundDeckCode(
  input: string
): Promise<CardCodeEntry[]> {
  // Dynamic import to keep the library out of the main bundle
  const { getDeckFromCode } = await import(
    "@piltoverarchive/riftbound-deck-codes"
  );
  const decoded = getDeckFromCode(input.trim());

  const entries: CardCodeEntry[] = [];

  // Handle chosen champion as Legend location
  if (decoded.chosenChampion) {
    const { setCode, number } = parseCardCode(decoded.chosenChampion);
    // Find the champion in mainDeck to get its count, default to 1
    const championInDeck = decoded.mainDeck.find(
      (c) => c.cardCode === decoded.chosenChampion
    );
    entries.push({
      setCode,
      number,
      quantity: championInDeck?.count ?? 1,
      location: "Legend",
    });
  }

  // Map main deck cards to Mainboard (excluding the chosen champion which is already in Legend)
  for (const card of decoded.mainDeck) {
    if (decoded.chosenChampion && card.cardCode === decoded.chosenChampion) {
      continue; // Already added as Legend
    }
    const { setCode, number } = parseCardCode(card.cardCode);
    entries.push({
      setCode,
      number,
      quantity: card.count,
      location: "Mainboard",
    });
  }

  // Map sideboard cards
  for (const card of decoded.sideboard) {
    const { setCode, number } = parseCardCode(card.cardCode);
    entries.push({
      setCode,
      number,
      quantity: card.count,
      location: "Sideboard",
    });
  }

  return entries;
}
